import type { MrpIncomingRow } from "@/lib/mrp";
import { isPrepackPodBoxLabelSku } from "../../../../src/mrp/prepack-pod-box-label-sku";
import { isPrepackPhysicalBoxStationStockSku } from "../../../../src/manufacturing/station-stock/prepack-physical-box-sku";
import { isPrepackPrintOutInstructionSku } from "../../../../src/manufacturing/station-stock/print-out-instruction-sku";
import { isPreEncapYearLabelStationStockSku } from "../../../../src/manufacturing/station-stock/year-label-sku";
import { isFinalQcORingStationStockSku } from "../../../../src/manufacturing/station-stock/final-qc-o-ring-sku";
import { isFinalQcResetLabelStationStockSku } from "../../../../src/manufacturing/station-stock/final-qc-reset-label-sku";
import { isFinalQcZipTieStationStockSku } from "../../../../src/manufacturing/station-stock/final-qc-zip-tie-sku";
import { isConfigurationStationStockSku } from "../../../../src/manufacturing/station-stock/configuration-station-stock-sku";

export interface KittingCartSlot {
  slotKey: string;
  kind: "work_order" | "transfer_order" | "batch";
  titleLine: string;
  headlineLabel: string;
  summaryLine: string;
  routeLine: string | null;
  dueHint: string | null;
  /** `YYYY-MM-DD` for cart strip sort; null when due is N/A. */
  scheduleDay: string | null;
  statusRaw: string;
  statusLabel: string;
  openLineCount: number;
  unitsDue: number;
  colorIndex: number;
  /** BOM kit destination station (`kit_to_station_id`); null when unassigned. */
  deliveryStationId: number | null;
  /** Floor delivery destination label (station name or "Unassigned"). */
  deliveryStationName: string;
  /** Distinct `public.batch.id` values via `batch_item` (work order and batch cards). */
  batchIds: number[];
  workOrderNumber?: string | null;
  workOrderId?: number | null;
  workOrderMvId?: number | null;
  workOrderMvNumber?: string | null;
  /** Linked work orders on batch cards (peek secondary detail). */
  linkedWorkOrderNumbers?: string[];
  linkedWorkOrderIds?: number[];
  transferOrderId?: number | null;
  transferNumber?: string | null;
}

/** URL-safe cart slot suffix for a BOM kit destination station. */
export const KITTING_UNASSIGNED_STATION_SLOT_SUFFIX = "station-unassigned";

export function kittingEntryLocationSlotSuffix(kitToStationId: number | null | undefined): string {
  if (kitToStationId == null || !Number.isFinite(kitToStationId)) {
    return KITTING_UNASSIGNED_STATION_SLOT_SUFFIX;
  }
  return `station-${Math.round(kitToStationId)}`;
}

export function kittingEntryLocationLabel(kitToStationName: string | null | undefined): string {
  const trimmed = kitToStationName != null ? String(kitToStationName).trim() : "";
  return trimmed.length > 0 ? trimmed : "Unassigned";
}

export function kittingEntryLocationFromIncomingRow(
  row: Pick<MrpIncomingRow, "kit_to_station_id" | "kit_to_station_name">,
): { stationId: number | null; stationName: string } {
  const stationId =
    row.kit_to_station_id != null && Number.isFinite(row.kit_to_station_id)
      ? Math.round(row.kit_to_station_id)
      : null;
  return {
    stationId,
    stationName: kittingEntryLocationLabel(row.kit_to_station_name),
  };
}

export function kittingAppendEntryLocationToSlotKey(
  baseSlotKey: string,
  kitToStationId: number | null | undefined,
): string {
  return `${baseSlotKey}:${kittingEntryLocationSlotSuffix(kitToStationId)}`;
}

function batchHeadlineWithDestination(batchId: number, deliveryStationName: string): string {
  return `Batch ${batchId} · ${deliveryStationName}`;
}

function workOrderHeadlineWithDestination(workOrderLabel: string, deliveryStationName: string): string {
  return `Work order ${workOrderLabel} · ${deliveryStationName}`;
}

/** Ascending numeric batch ids from incoming `batch_ids`. */
export function kittingSortedBatchIds(batchIds: number[] | null | undefined): number[] {
  return (batchIds ?? []).filter((id) => Number.isFinite(id)).sort((left, right) => left - right);
}

/** Lowest linked batch id when a work order has any `batch_item` association. */
export function kittingPreferredBatchId(batchIds: number[] | null | undefined): number | null {
  const sorted = kittingSortedBatchIds(batchIds);
  return sorted.length > 0 ? sorted[0]! : null;
}

function incomingScheduleDay(value: string | null | undefined): string | null {
  if (value == null || String(value).trim() === "") return null;
  const trimmed = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  return trimmed;
}

export function formatKittingScheduleDueLabel(day: string | null | undefined): string {
  const normalized = incomingScheduleDay(day);
  if (normalized == null) return "N/A";
  try {
    return `Due ${new Date(`${normalized}T12:00:00`).toLocaleDateString()}`;
  } catch {
    return `Due ${normalized}`;
  }
}

export interface KittingCartSchedule {
  dueHint: string | null;
  scheduleDay: string | null;
}

/** Ascending by schedule day; jobs without a date sort last. */
export function compareKittingCartSlotsByDueDate(
  left: Pick<KittingCartSlot, "scheduleDay" | "titleLine">,
  right: Pick<KittingCartSlot, "scheduleDay" | "titleLine">,
): number {
  const leftDay = left.scheduleDay;
  const rightDay = right.scheduleDay;
  if (leftDay == null && rightDay == null) {
    return left.titleLine.localeCompare(right.titleLine, undefined, { sensitivity: "base" });
  }
  if (leftDay == null) return 1;
  if (rightDay == null) return -1;
  const byDate = leftDay.localeCompare(rightDay);
  if (byDate !== 0) return byDate;
  return left.titleLine.localeCompare(right.titleLine, undefined, { sensitivity: "base" });
}

/** Work orders: earliest linked batch `mfr_ship_by_date`, or N/A when none linked / unset. */
export function kittingWorkOrderCartSchedule(
  batchIds: number[],
  batchMfrShipByById: ReadonlyMap<number, string | null> | undefined,
): KittingCartSchedule {
  if (batchIds.length === 0) {
    return { dueHint: "N/A", scheduleDay: null };
  }
  const days: string[] = [];
  for (const batchId of batchIds) {
    const normalized = incomingScheduleDay(batchMfrShipByById?.get(batchId));
    if (normalized != null) days.push(normalized);
  }
  days.sort();
  const earliest = days[0] ?? null;
  return { dueHint: formatKittingScheduleDueLabel(earliest), scheduleDay: earliest };
}

export function kittingBatchCartSchedule(
  batchId: number,
  batchMfrShipByById: ReadonlyMap<number, string | null> | undefined,
): KittingCartSchedule {
  const day = incomingScheduleDay(batchMfrShipByById?.get(batchId));
  return { dueHint: formatKittingScheduleDueLabel(day), scheduleDay: day };
}

export function kittingTransferCartSchedule(rows: MrpIncomingRow[]): KittingCartSchedule {
  const dates = rows
    .map((row) => incomingScheduleDay(row.expected_arrival))
    .filter((value): value is string => value != null);
  dates.sort();
  const day = dates[0] ?? null;
  return { dueHint: formatKittingScheduleDueLabel(day), scheduleDay: day };
}

export function workOrderIncomingGroupKey(row: MrpIncomingRow): string {
  const numberPart = row.po_number != null ? row.po_number.trim() : "";
  if (numberPart.length > 0) return `wo:${numberPart}`;
  if (row.kind === "work_order" && row.work_order_mv_id != null) {
    return `mv:${String(row.work_order_mv_id)}`;
  }
  return `row:${row.id}`;
}

function workOrderDisplayLabel(rows: MrpIncomingRow[], groupKey: string): string {
  const explicit = rows.find((row) => row.po_number != null && row.po_number.trim() !== "")?.po_number?.trim();
  if (explicit) return explicit;
  if (groupKey.startsWith("mv:")) return `MV ${groupKey.slice(3)}`;
  return "Work order";
}

function humanizeStatusLabel(raw: string): string {
  const t = raw.trim();
  if (t.length === 0) return "Active";
  return t
    .split(/_+/g)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * SKUs handled at floor stations (POD labels, station prestock cartons, instruction sheets,
 * year labels, Final QC zip ties, reset labels, and O-rings, Configuration SIM/tray).
 * Work-order kitting does not pick these. Transfer lines still do: the goods are leaving SGS.
 */
export function isStationPrestockKittingExcludedSku(sku: string): boolean {
  return (
    isPrepackPodBoxLabelSku(sku) ||
    isPrepackPrintOutInstructionSku(sku) ||
    isPrepackPhysicalBoxStationStockSku(sku) ||
    isPreEncapYearLabelStationStockSku(sku) ||
    isFinalQcZipTieStationStockSku(sku) ||
    isFinalQcResetLabelStationStockSku(sku) ||
    isFinalQcORingStationStockSku(sku) ||
    isConfigurationStationStockSku(sku)
  );
}

export function kittingRowExcludedAsStationPrestock(row: MrpIncomingRow): boolean {
  if (row.kind === "transfer_order") return false;
  return isStationPrestockKittingExcludedSku(String(row.cpn ?? ""));
}

function transferLegExcludedFromKitting(row: MrpIncomingRow): boolean {
  if (row.kind !== "transfer_order") return false;
  const lower = (row.transfer_status ?? "").trim().toLowerCase();
  return lower === "shipped" || lower === "delivered";
}

/**
 * Positive pick units for kitting.
 * Outbound transfer legs leave SGS (role "from", negative remaining).
 * Inbound legs arrive at SGS (role "to", positive remaining) and are not picked here.
 */
export function kittingIncomingDemandQuantity(row: MrpIncomingRow): number {
  const raw = Math.round(Number(row.quantity_remaining));
  if (row.kind === "transfer_order") {
    if (row.transfer_role === "to") return 0;
    if (row.transfer_role === "from" || raw < 0) return Math.abs(raw);
    return 0;
  }
  return raw > 0 ? raw : 0;
}

export function incomingRowsEligibleForKitting(rows: MrpIncomingRow[]): MrpIncomingRow[] {
  return rows.filter(
    (row) =>
      kittingIncomingDemandQuantity(row) > 0 &&
      !transferLegExcludedFromKitting(row) &&
      !kittingRowExcludedAsStationPrestock(row),
  );
}

/** Rows an open pick run may still show after demand has left the queue. */
export function incomingRowsHeldForOpenKittingPickRun(rows: MrpIncomingRow[]): MrpIncomingRow[] {
  return rows.filter(
    (row) => !transferLegExcludedFromKitting(row) && !kittingRowExcludedAsStationPrestock(row),
  );
}

/** Label for a run slot that is no longer in the incoming feed. */
export function kittingFallbackCartSlotForRunKey(slotKey: string): KittingCartSlot | null {
  const trimmed = slotKey.trim();
  if (trimmed.length === 0) return null;

  if (trimmed.startsWith("tr-")) {
    const transferOrderId = Number.parseInt(trimmed.slice(3).split(":")[0] ?? "", 10);
    const title = Number.isFinite(transferOrderId) && transferOrderId > 0 ? `T-${transferOrderId}` : trimmed;
    return {
      slotKey: trimmed,
      kind: "transfer_order",
      titleLine: title,
      headlineLabel: `Transfer ${title}`,
      summaryLine: "Picked, waiting to finish",
      routeLine: null,
      dueHint: null,
      scheduleDay: null,
      statusRaw: "picked",
      statusLabel: "Picked",
      openLineCount: 0,
      unitsDue: 0,
      colorIndex: 0,
      deliveryStationId: null,
      deliveryStationName: "SGS",
      batchIds: [],
      transferOrderId: Number.isFinite(transferOrderId) ? transferOrderId : null,
      transferNumber: title,
    };
  }

  const stationSep = trimmed.lastIndexOf(":station-");
  if (stationSep === -1) return null;
  const base = trimmed.slice(0, stationSep);
  const stationSuffix = trimmed.slice(stationSep + 1);
  const deliveryStationId =
    stationSuffix === KITTING_UNASSIGNED_STATION_SLOT_SUFFIX
      ? null
      : Number.parseInt(stationSuffix.slice("station-".length), 10);
  const deliveryStationName =
    deliveryStationId == null || !Number.isFinite(deliveryStationId)
      ? "Unassigned"
      : `Station ${String(deliveryStationId)}`;

  if (base.startsWith("batch-")) {
    const batchId = Number.parseInt(base.slice("batch-".length), 10);
    if (!Number.isFinite(batchId) || batchId <= 0) return null;
    return {
      slotKey: trimmed,
      kind: "batch",
      titleLine: String(batchId),
      headlineLabel: `Batch ${String(batchId)} · ${deliveryStationName}`,
      summaryLine: "Picked, waiting to finish",
      routeLine: `Deliver to ${deliveryStationName}`,
      dueHint: null,
      scheduleDay: null,
      statusRaw: "picked",
      statusLabel: "Picked",
      openLineCount: 0,
      unitsDue: 0,
      colorIndex: 0,
      deliveryStationId: Number.isFinite(deliveryStationId) ? deliveryStationId : null,
      deliveryStationName,
      batchIds: [batchId],
    };
  }

  if (base.startsWith("wo-")) {
    const groupTail = base.slice("wo-".length);
    const title = groupTail.startsWith("wo:") ? groupTail.slice(3) : groupTail;
    if (title.length === 0) return null;
    return {
      slotKey: trimmed,
      kind: "work_order",
      titleLine: title,
      headlineLabel: `Work order ${title} · ${deliveryStationName}`,
      summaryLine: "Picked, waiting to finish",
      routeLine: `Deliver to ${deliveryStationName}`,
      dueHint: null,
      scheduleDay: null,
      statusRaw: "picked",
      statusLabel: "Picked",
      openLineCount: 0,
      unitsDue: 0,
      colorIndex: 0,
      deliveryStationId: Number.isFinite(deliveryStationId) ? deliveryStationId : null,
      deliveryStationName,
      batchIds: [],
      workOrderNumber: title,
    };
  }

  return null;
}

function sumUnitsDue(rows: MrpIncomingRow[]): number {
  let total = 0;
  for (const row of rows) {
    total += kittingIncomingDemandQuantity(row);
  }
  return total;
}

function buildBatchKittingCartSlots(
  rows: MrpIncomingRow[],
  batchMfrShipByById: ReadonlyMap<number, string | null> | undefined,
): Omit<KittingCartSlot, "colorIndex">[] {
  const woRows = rows.filter((row) => row.kind === "work_order" && row.location_code === "sgs");
  const byBatchAndStation = new Map<
    string,
    {
      batchId: number;
      deliveryStationId: number | null;
      deliveryStationName: string;
      cpns: Set<string>;
      woLabels: Set<string>;
      unitRows: MrpIncomingRow[];
    }
  >();
  for (const row of woRows) {
    const ids = row.batch_ids ?? [];
    if (ids.length === 0) continue;
    const { stationId, stationName } = kittingEntryLocationFromIncomingRow(row);
    const woLabel =
      row.po_number != null && row.po_number.trim() !== "" ? row.po_number.trim() : "Work order";
    const cpn = String(row.cpn ?? "").trim();
    for (const batchId of ids) {
      if (!Number.isFinite(batchId)) continue;
      const bucketKey = `${batchId}:${kittingEntryLocationSlotSuffix(stationId)}`;
      const bucket = byBatchAndStation.get(bucketKey) ?? {
        batchId,
        deliveryStationId: stationId,
        deliveryStationName: stationName,
        cpns: new Set<string>(),
        woLabels: new Set<string>(),
        unitRows: [],
      };
      if (cpn.length > 0) bucket.cpns.add(cpn);
      bucket.woLabels.add(woLabel);
      if (kittingIncomingDemandQuantity(row) > 0) bucket.unitRows.push(row);
      byBatchAndStation.set(bucketKey, bucket);
    }
  }
  const slots: Omit<KittingCartSlot, "colorIndex">[] = [];
  for (const bucket of byBatchAndStation.values()) {
    const batchId = bucket.batchId;
    const woList = Array.from(bucket.woLabels).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
    const woLine =
      woList.length <= 2 ? woList.join(", ") : `${woList.slice(0, 2).join(", ")} +${woList.length - 2} more`;
    const deliveryStationName = bucket.deliveryStationName;
    slots.push({
      slotKey: kittingAppendEntryLocationToSlotKey(`batch-${batchId}`, bucket.deliveryStationId),
      kind: "batch",
      titleLine: String(batchId),
      headlineLabel: batchHeadlineWithDestination(batchId, deliveryStationName),
      summaryLine: `${bucket.cpns.size} open component SKU${bucket.cpns.size === 1 ? "" : "s"}`,
      routeLine: `Deliver to ${deliveryStationName} · Work orders: ${woLine}`,
      ...kittingBatchCartSchedule(batchId, batchMfrShipByById),
      statusRaw: "open_demand",
      statusLabel: "Open demand",
      openLineCount: bucket.cpns.size,
      unitsDue: sumUnitsDue(bucket.unitRows),
      deliveryStationId: bucket.deliveryStationId,
      deliveryStationName,
      batchIds: [batchId],
      linkedWorkOrderNumbers: woList,
    });
  }
  return slots;
}

export function buildKittingCartSlots(
  rows: MrpIncomingRow[],
  options?: {
    batchMfrShipByById?: ReadonlyMap<number, string | null>;
    /**
     * Keep a cart slot when every line is already picked. An open pick run is not
     * delivered until Finish, so those jobs must stay reopenable.
     */
    retainZeroDemandSlots?: boolean;
  },
): KittingCartSlot[] {
  const batchMfrShipByById = options?.batchMfrShipByById;
  const slots: Omit<KittingCartSlot, "colorIndex">[] = [];

  const woRows = rows.filter((row) => row.kind === "work_order" && row.location_code === "sgs");
  const woGroups = new Map<string, MrpIncomingRow[]>();
  for (const row of woRows) {
    const key = workOrderIncomingGroupKey(row);
    const list = woGroups.get(key) ?? [];
    list.push(row);
    woGroups.set(key, list);
  }
  for (const [groupKey, list] of woGroups) {
    const openRows = list.filter((row) => kittingIncomingDemandQuantity(row) > 0);
    const batchIdSet = new Set<number>();
    for (const row of list) {
      for (const batchId of row.batch_ids ?? []) {
        if (Number.isFinite(batchId)) batchIdSet.add(batchId);
      }
    }
    const batchIds = Array.from(batchIdSet).sort((a, b) => a - b);
    if (batchIds.length > 0) {
      continue;
    }
    const stationGroups = new Map<
      string,
      {
        deliveryStationId: number | null;
        deliveryStationName: string;
        rows: MrpIncomingRow[];
      }
    >();
    const stationSource =
      openRows.length > 0 ? openRows : options?.retainZeroDemandSlots === true ? list : [];
    for (const row of stationSource) {
      const { stationId, stationName } = kittingEntryLocationFromIncomingRow(row);
      const stationKey = kittingEntryLocationSlotSuffix(stationId);
      const bucket = stationGroups.get(stationKey) ?? {
        deliveryStationId: stationId,
        deliveryStationName: stationName,
        rows: [],
      };
      bucket.rows.push(row);
      stationGroups.set(stationKey, bucket);
    }
    for (const stationBucket of stationGroups.values()) {
      const distinctCpns = new Set(stationBucket.rows.map((row) => row.cpn)).size;
      const title = workOrderDisplayLabel(list, groupKey);
      const deliveryStationName = stationBucket.deliveryStationName;
      const workOrderId = list.find((row) => row.work_order_id != null)?.work_order_id ?? null;
      const workOrderMvId =
        list.find((row) => row.work_order_mv_id != null)?.work_order_mv_id ?? null;
      const workOrderMvNumber =
        list.find((row) => row.work_order_mv_number != null && row.work_order_mv_number.trim() !== "")
          ?.work_order_mv_number?.trim() ?? null;
      slots.push({
        slotKey: kittingAppendEntryLocationToSlotKey(`wo-${groupKey}`, stationBucket.deliveryStationId),
        kind: "work_order",
        titleLine: title,
        headlineLabel: workOrderHeadlineWithDestination(title, deliveryStationName),
        summaryLine: `${distinctCpns} open line${distinctCpns === 1 ? "" : "s"}`,
        routeLine: `Deliver to ${deliveryStationName}`,
        ...kittingWorkOrderCartSchedule(batchIds, batchMfrShipByById),
        statusRaw: "open_demand",
        statusLabel: "Open demand",
        openLineCount: distinctCpns,
        unitsDue: sumUnitsDue(stationBucket.rows),
        deliveryStationId: stationBucket.deliveryStationId,
        deliveryStationName,
        batchIds,
        workOrderNumber: title,
        workOrderId,
        workOrderMvId,
        workOrderMvNumber,
      });
    }
  }

  const trRows = rows.filter((row) => row.kind === "transfer_order" && row.location_code === "sgs");
  const trGroups = new Map<number, MrpIncomingRow[]>();
  for (const row of trRows) {
    const transferOrderId = row.transfer_order_id;
    if (transferOrderId == null) continue;
    const bucket = trGroups.get(transferOrderId) ?? [];
    bucket.push(row);
    trGroups.set(transferOrderId, bucket);
  }
  for (const [transferOrderId, list] of trGroups) {
    const openRows = list.filter((row) => kittingIncomingDemandQuantity(row) > 0);
    const slotRows =
      openRows.length > 0 ? openRows : options?.retainZeroDemandSlots === true ? list : [];
    if (slotRows.length === 0) continue;
    const transferNumber = list[0]!.transfer_number?.trim() || `T-${transferOrderId}`;
    const transferHeadline = transferNumber.match(/^T-/i) ? transferNumber : `T-${transferNumber}`;
    const distinctLines = new Set(
      slotRows.map((row) => (row.transfer_group_id != null ? row.transfer_group_id : row.id)),
    ).size;
    const fromLeg = list.find((row) => row.transfer_role === "from");
    const toLeg = list.find((row) => row.transfer_role === "to");
    let routeLine: string | null = null;
    if (fromLeg != null && toLeg == null) {
      routeLine = `Ship toward ${fromLeg.to_location_label ?? "destination"}`;
    } else if (toLeg != null && fromLeg == null) {
      routeLine = `Receive from ${toLeg.from_location_label ?? "origin"}`;
    } else if (fromLeg != null && toLeg != null) {
      routeLine = `${fromLeg.from_location_label ?? "Origin"} → ${toLeg.to_location_label ?? "Destination"}`;
    }
    const st = list[0]!.transfer_status ?? "";
    slots.push({
      slotKey: `tr-${transferOrderId}`,
      kind: "transfer_order",
      titleLine: transferHeadline,
      headlineLabel: `Transfer ${transferHeadline}`,
      summaryLine: `${distinctLines} line${distinctLines === 1 ? "" : "s"} at SGS`,
      routeLine,
      ...kittingTransferCartSchedule(list),
      statusRaw: st.length > 0 ? st : "active",
      statusLabel: humanizeStatusLabel(st.length > 0 ? st : "active"),
      openLineCount: distinctLines,
      unitsDue: sumUnitsDue(openRows),
      deliveryStationId: null,
      deliveryStationName: toLeg?.to_location_label?.trim() || fromLeg?.to_location_label?.trim() || "SGS",
      batchIds: [],
      transferOrderId,
      transferNumber: transferHeadline,
    });
  }

  slots.push(...buildBatchKittingCartSlots(rows, batchMfrShipByById));

  slots.sort(compareKittingCartSlotsByDueDate);

  return slots.map((slot, colorIndex) => ({ ...slot, colorIndex }));
}

export function kittingCartSlotById(slots: KittingCartSlot[]): Map<string, KittingCartSlot> {
  return new Map(slots.map((slot) => [slot.slotKey, slot]));
}

/** Floor pick "Put in cart bin" pill accent by destination type (not per-order colorIndex). */
export const KITTING_DESTINATION_ACCENT: Record<KittingCartSlot["kind"], string> = {
  batch: "border-l-violet-500 bg-violet-50/80",
  transfer_order: "border-l-sky-500 bg-sky-50/80",
  work_order: "border-l-amber-500 bg-amber-50/80",
};

export function kittingDestinationAccentClass(kind: KittingCartSlot["kind"]): string {
  return KITTING_DESTINATION_ACCENT[kind];
}
